import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PhoneOutgoing, AlertTriangle, Plug, Ban } from "lucide-react";
import { PageHeader, Button, Input, Select, Modal, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type OutboundCall, type OutboundConfig } from "@/lib/api";

const PURPOSE_LABEL: Record<string, string> = {
  lead_recovery: "Lead recovery", appointment_confirmation: "Appointment confirmation",
  estimate_followup: "Estimate follow-up", reactivation: "Reactivation", customer_callback: "Customer callback",
};
const STATUS_TONE: Record<string, "slate" | "blue" | "green" | "amber" | "red"> = {
  queued: "blue", dialing: "blue", completed: "green", failed: "red", unavailable: "amber", cancelled: "slate",
};

export default function OutboundCalls() {
  const [config, setConfig] = useState<OutboundConfig | null>(null);
  const [rows, setRows] = useState<OutboundCall[] | null>(null);
  const [showEnqueue, setShowEnqueue] = useState(false);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(() => {
    api.outboundList().then(setRows).catch((e) => toast.error(formatApiError(e)));
    api.outboundConfig().then(setConfig).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setProcessing(true);
    try { const r = await api.outboundProcess(); toast.success(`Processed ${r.scanned} queued call(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setProcessing(false); }
  };
  const cancel = async (id: string) => {
    try { await api.outboundCancel(id); toast.success("Call cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="page-outbound">
      <PageHeader title="Outbound AI Calling" description="Queue AI callbacks for recovery, confirmations and follow-ups — placed only when your voice provider is connected." testid="outbound-header"
        action={config?.included ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={runNow} disabled={processing} data-testid="outbound-process-btn">{processing ? "Processing…" : "Process queue"}</Button>
            <Button onClick={() => setShowEnqueue(true)} data-testid="outbound-enqueue-btn"><PhoneOutgoing className="h-4 w-4" /> Queue a call</Button>
          </div>
        ) : undefined} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="outbound-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.included ? <StatusBadge tone="green">Included · {config.usage_this_month}/{config.allowance} calls this month</StatusBadge>
            : <StatusBadge tone="amber">Available on Pro</StatusBadge>}
          {config.included && (config.provider_configured
            ? <StatusBadge tone="green"><Plug className="mr-1 inline h-3 w-3" /> {config.provider} connected</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> Voice provider: configuration required</StatusBadge>)}
          {config.included && <span className="text-xs text-slate-500">Calling window {config.window_start_hour}:00–{config.window_end_hour}:00 {config.timezone}</span>}
        </div>
      )}

      {config && !config.included && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700" data-testid="outbound-upgrade">
          Outbound AI calling is included on the Pro plan (with a monthly allowance) and available as high-volume/custom on Custom plans. Upgrade to enable AI callbacks.
        </div>
      )}
      {config?.included && !config?.provider_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="outbound-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">Your voice provider (Retell) isn't connected yet, so queued calls are marked <b>“Unavailable — Configuration Required”</b> and are <b>never</b> dialed or recorded as completed. Connect the provider to start placing real calls — no rebuild needed.</p>
        </div>
      )}

      {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
        <EmptyState icon={PhoneOutgoing} title="No calls queued" description="Queue an AI callback to a contact for one of your approved purposes." />
      ) : (
        <Card><CardContent className="overflow-x-auto p-0" data-testid="outbound-table">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500"><tr>
              <th className="px-4 py-2.5">Contact</th><th className="px-4 py-2.5">Purpose</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Outcome</th><th className="px-4 py-2.5">Queued</th><th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`outbound-row-${c.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{c.contact_name}</div><div className="text-xs text-slate-400">{c.phone}</div></td>
                  <td className="px-4 py-3 text-slate-600">{PURPOSE_LABEL[c.purpose] ?? c.purpose}</td>
                  <td className="px-4 py-3"><StatusBadge tone={STATUS_TONE[c.status] ?? "slate"}>{c.status}</StatusBadge></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(c.outcome ?? "—").replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">{(c.status === "queued" || c.status === "unavailable") && <Button size="sm" variant="ghost" onClick={() => cancel(c.id)} data-testid={`outbound-cancel-${c.id}`}><Ban className="h-3.5 w-3.5 text-red-500" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {showEnqueue && config && <EnqueueModal purposes={config.purposes} onClose={() => setShowEnqueue(false)} onDone={load} />}
    </div>
  );
}

function EnqueueModal({ purposes, onClose, onDone }: { purposes: string[]; onClose: () => void; onDone: () => void }) {
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactId, setContactId] = useState("");
  const [purpose, setPurpose] = useState(purposes[0] ?? "customer_callback");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.crmContacts().then((d: any) => setContacts(d.filter((c: any) => c.phone && !c.do_not_call))).catch(() => {}); }, []);
  const contactOpts = useMemo(() => contacts.map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name ?? ""} · ${c.phone}` })), [contacts]);
  const purposeOpts = purposes.map((p) => ({ value: p, label: PURPOSE_LABEL[p] ?? p }));

  const submit = async () => {
    if (!contactId) { toast.error("Pick a contact"); return; }
    setBusy(true);
    try { await api.outboundEnqueue({ contact_id: contactId, purpose }); toast.success("Call queued"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Queue an AI call" description="Only contacts with a phone number who haven't opted out of calls are shown." testid="outbound-enqueue-modal">
      <div className="space-y-3">
        <div><p className="mb-1 text-xs font-medium text-slate-500">Contact</p><Select value={contactId} onChange={setContactId} options={contactOpts} placeholder="Select a contact" testid="outbound-contact-select" /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Purpose</p><Select value={purpose} onChange={setPurpose} options={purposeOpts} testid="outbound-purpose-select" /></div>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="outbound-enqueue-submit"><PhoneOutgoing className="h-4 w-4" /> Queue call</Button>
      </div>
    </Modal>
  );
}
